/**
 * @file components/PrimaryContent/component.js
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 * @author Abby Christens [abby.christens@cbc.ca]
 */

import { Label } from "../../../../../Label/component";
import LinkWrap from "../../../../../LinkWrap/component";
import classNames from "classnames";

import Image from "../../../../../ImageWrapper/component";
import CardText from "../../../CardText/component";
import Headline from "../../../Headline/component";

import Metadata from "../../../Metadata/component";
import Deck from "../../../../../../pages/Detail/components/Deck/component";
import Bulletpoint from "./components/Bulletpoint/component";

/**
 * The PrimaryContent component.
 *
 * Component will display primary Content Package data
 *
 * @param {Object} props The component props
 * @returns {React.Component} Returns the PrimaryContent component
 */
export default function PrimaryContent({
    title = "",
    deck = "",
    flag = "",
    headlineId = "",
    id = "",
    external = false,
    primary = null,
    baseSection = "",
    subSection = "",
    updateTime = null,
    metadataId = "",
    url = "",
    className,
    headlineClasses,
    itemType,
    media,
    imageURL,
}) {
    const labelProps = {
        flag: flag,
        external: external,
        id: id,
    };

    const metaProps = {
        baseSection: baseSection,
        subSection: subSection,
        updated: updateTime,
        showTimestamp: true,
        id: metadataId,
        url: url,
    };

    const linkProps = {
        "data-contentid": metadataId,
        id: metadataId,
        external: external,
        url: url,
        "data-cy": `type-${itemType}`,
    };
    const classes = classNames("primaryContent", className);

    return (
        <div className={classes} data-testid="primary-content">
            <div
                className="primaryHeadline mobileHeadline"
                data-feature-instance="primary-headline"
            >
                <LinkWrap className={headlineClasses} key={id} {...linkProps}>
                    <Headline id={headlineId} text={title} />
                </LinkWrap>
            </div>
            <div className="supportingContent">
                <Deck text={deck} />
                <div className="labelMetaData">
                    <Label className="primaryLabel" {...labelProps} />
                    <Metadata {...metaProps} />
                    <Bulletpoints list={primary?.bulletpoints} />
                    <Relatedlinks list={primary?.relatedLinks} />
                </div>
            </div>
            <div className="primaryImage">
                {getCardLeadMedia(media, imageURL, linkProps)}
            </div>
        </div>
    );
}

/**
 * Get the lead media to display, either an image or a video
 * @param {object} props Card properties
 * @returns {jsx} Return the JSX to render an image or a video
 */
function getCardLeadMedia(media, imageURL, linkProps) {
    // If it's a video lead media
    if (media) {
        return media;
    }

    return (
        <LinkWrap
            className="primaryImageLink"
            data-feature-instance="primary-visual"
            {...linkProps}
        >
            <Image url={imageURL} loading={null} isAboveFold={true} />
        </LinkWrap>
    );
}

export const Bulletpoints = ({ list }) => {
    if (!list?.length) {
        return null;
    }
    return (
        <ul className="contentPackageList">
            {list.map(({ text }, idx) => (
                <Bulletpoint key={`${text}-${idx}`} text={text} />
            ))}
        </ul>
    );
};

export const Relatedlinks = ({ list }) => {
    if (!list?.length) {
        return null;
    }
    return list.map(({ url, external, title, key, flag }, idx) => {
        return (
            <div
                key={`${key}-${idx}`}
                data-feature-instance="primary-related"
                data-feature-position={idx + 1}
                data-testid="primary-content-attribution"
            >
                <CardText
                    key={`${key}-${idx}`}
                    url={url}
                    external={external}
                    title={title}
                    className="relatedCard"
                    departments={{}}
                    flag={flag}
                />
            </div>
        );
    });
};
